import React, { useState, useEffect } from "react";
import axios from "axios";

const AdsPositionTool = () => {
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");

  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [preventFetch, setPreventFetch] = useState(false); // Flag to stop redundant suggestion calls

  const [keywordsData, setKeywordsData] = useState([]);
  const [adsData, setAdsData] = useState([]);
  const [inlineProducts, setInlineProducts] = useState([]);
  const [shoppingResults, setShoppingResults] = useState([]);
  const [organicResults, setOrganicResults] = useState([]);

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Only show empty alerts after a search
  const [error, setError] = useState("");

  // 🔥 Debounce location search
  useEffect(() => {
    if (preventFetch) {
      setPreventFetch(false);
      return;
    }

    const delayDebounce = setTimeout(() => {
      if (location.trim().length >= 3) {
        fetchLocationSuggestions(location);
      } else {
        setLocationSuggestions([]);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [location]);

  const fetchLocationSuggestions = async (input) => {
    try {
      const res = await axios.post(
        "https://api.confidanto.com/search-locations/search",
        { input },
      );
      setLocationSuggestions(res.data || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error("Location suggestion error", err);
    }
  };

  const handleSearch = async () => {
    if (!query.trim() || !location.trim()) {
      setError("Please enter keyword and location");
      return;
    }

    setLoading(true);
    setError("");
    setHasSearched(true);

    try {
      const response = await axios.post(
        "https://api.confidanto.com/search_ads_serapi",
        {
          query,
          location,
          hl: "en",
          gl: "in",
          deviceType: "Desktop",
        },
      );

      const data = response?.data || {};

      if (data.keywordData === "No data found" || !data.keywordData) {
        setKeywordsData([]);
      } else {
        setKeywordsData([data.keywordData]);
      }

      const serp = data.serpapi_res || {};

      setAdsData(serp.ads || []);
      setInlineProducts(serp.inline_products || []);
      setShoppingResults(serp.shopping_results || []);
      setOrganicResults(serp.organic_results || []);
    } catch (err) {
      setError("Something went wrong while fetching data.");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-6xl mx-auto">
        {/* Search Card Section */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-800">
            Ads Position Tool
          </h1>
          <p className="text-gray-500 mt-2">
            Analyze Google SERP ads & organic ranking positions
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            {/* Keyword Input */}
            <div className="md:col-span-5">
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                Keyword
              </label>
              <div className="relative flex items-center">
                <div className="absolute left-3.5 z-10 text-gray-400 pointer-events-none">
                  {/* FIXED SVG PATH BELOW */}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="e.g. coffee"
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Location Input */}
            <div className="md:col-span-5 relative">
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                Location
              </label>
              <div className="relative flex items-center">
                <div className="absolute left-3.5 z-10 text-gray-400 pointer-events-none">
                  {/* FIXED SVG PATHS BELOW */}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Enter location"
                  className="w-full pl-10 pr-9 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  onFocus={() => {
                    if (locationSuggestions.length > 0)
                      setShowSuggestions(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                />
                <div className="absolute right-3.5 z-10 text-gray-400 pointer-events-none">
                  {/* FIXED SVG PATH BELOW */}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {/* Suggestions Dropdown */}
              {showSuggestions && locationSuggestions.length > 0 && (
                <ul className="absolute left-0 right-0 bg-white border border-gray-200 mt-1 rounded-xl shadow-lg max-h-52 overflow-y-auto z-50 py-1 text-sm">
                  {locationSuggestions.map((loc, index) => (
                    <li
                      key={index}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-700 transition"
                      onMouseDown={() => {
                        setPreventFetch(true);
                        setLocation(loc);
                        setShowSuggestions(false);
                        setLocationSuggestions([]);
                      }}
                    >
                      {loc}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Search Button */}
            <div className="md:col-span-2">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="w-full bg-[#2563EB] hover:bg-blue-700 active:bg-blue-800 text-white font-medium py-2.5 px-5 rounded-xl transition flex items-center justify-center gap-2 shadow-sm disabled:opacity-70"
              >
                {/* FIXED SVG PATH BELOW */}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Error Notice */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="flex justify-center items-center py-12 text-blue-600 gap-3 text-sm font-medium">
            <svg
              className="animate-spin h-5 w-5 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Fetching search engine results...
          </div>
        )}

        {/* Status Alert Banners */}
        {hasSearched && !loading && adsData.length === 0 && (
          <div className="space-y-3 mb-6">
            {/* Banner 1: No paid ads found */}
            <div className="bg-[#FFFDF0] border border-[#F6EEB4] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100/80 flex items-center justify-center text-amber-600 flex-shrink-0">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-amber-900">
                    No paid ads found
                  </h4>
                  <p className="text-xs text-amber-700/80 mt-0.5">
                    We couldn't find any paid advertisements for this keyword.
                  </p>
                </div>
              </div>
            </div>

            {/* Banner 2: Showing organic results */}
            <div className="bg-[#F0F7FF] border border-[#E0EEFF] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .416-.042.822-.122 1.215A1.5 1.5 0 0014.5 10H14a1 1 0 00-1 1v.586l-1.293 1.293a1 1 0 01-.707.293H10a1 1 0 01-1-1v-1a1 1 0 00-1-1H7a1 1 0 00-1 1v1a1 1 0 01-1 1h-.293a1 1 0 01-.707-.293l-1-1A5.99 5.99 0 014 10c0-.683.114-1.34.332-1.973z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-blue-950">
                    Showing organic results
                  </h4>
                  <p className="text-xs text-blue-700/80 mt-0.5">
                    No paid ads found. Displaying organic ranking results.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Paid Ads Section (If Available) */}
        {hasSearched && !loading && adsData.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-200">
              <h3 className="text-base font-bold text-gray-800">
                Paid Advertisements
              </h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                Total Ads: {adsData.length}
              </span>
            </div>
            <div className="space-y-4">
              {adsData.map((ad, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-gray-200/80 rounded-2xl p-5 shadow-sm"
                >
                  <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60 inline-block mb-2">
                    Ad #{idx + 1}
                  </span>
                  <a
                    href={ad.link}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-blue-700 font-bold hover:underline text-base"
                  >
                    {ad.title}
                  </a>
                  <p className="text-xs text-emerald-700 mt-1">
                    {ad.displayed_link || ad.link}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">{ad.snippet}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Organic Results Header & List */}
        {hasSearched && !loading && organicResults.length > 0 && (
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 pb-2 mb-6">
              <div className="relative">
                <h3 className="text-sm font-bold text-gray-900 pb-2">
                  Organic Results
                </h3>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full"></div>
              </div>
              <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                Total Results: {organicResults.length}
              </span>
            </div>

            {/* List of organic results */}
            <div className="space-y-5">
              {organicResults.map((result, index) => {
                const thumbnailImg = result.thumbnail || result.favicon;

                // Real data extraction from SerpAPI organic_results object
                const sourceName = result.source || "Organic Web";
                const regions =
                  result.about_this_result?.regions?.join(", ").toUpperCase() ||
                  "Global";
                const languages =
                  result.about_this_result?.languages
                    ?.join(", ")
                    .toUpperCase() || "EN";
                const highlights = result.snippet_highlighted_words
                  ? result.snippet_highlighted_words.slice(0, 2).join(", ")
                  : "None";
                const isSecure = result.link?.startsWith("https")
                  ? "HTTPS (Secure)"
                  : "HTTP";

                return (
                  <div
                    key={index}
                    className="bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                      {/* Main Left Content Column */}
                      <div className="lg:col-span-7 flex flex-col justify-between h-full">
                        <div>
                          {/* Rank Badge */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-[#1D4ED8] text-white text-[11px] font-bold px-2 py-0.5 rounded">
                              #{result.position || index + 1}
                            </span>
                            <span className="text-xs font-semibold text-gray-600">
                              Position {result.position || index + 1}
                            </span>
                          </div>

                          {/* Title */}
                          <h2 className="text-lg font-bold text-blue-700 hover:underline cursor-pointer leading-snug">
                            <a
                              href={result.link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {result.title}
                            </a>
                          </h2>

                          {/* Displayed Link with External Icon */}
                          <div className="flex items-center gap-1.5 mt-1 text-xs text-emerald-700 font-medium break-all">
                            <span>{result.displayed_link || result.link}</span>
                            <a
                              href={result.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-600 hover:text-emerald-800"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </a>
                          </div>

                          {/* Snippet */}
                          <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                            {result.snippet}
                          </p>

                          {/* Image preview if provided by API */}
                          {thumbnailImg && (
                            <div className="mt-4">
                              <img
                                src={thumbnailImg}
                                alt={result.title}
                                className="w-36 h-24 object-cover rounded-xl border border-gray-100 shadow-sm"
                              />
                            </div>
                          )}
                        </div>

                        {/* Timestamp Footer */}
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-5 pt-3 border-t border-gray-50">
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>Verified Search Result</span>
                        </div>
                      </div>

                      {/* Right Metrics Panel */}
                      <div className="lg:col-span-5 border-t lg:border-t-0 lg:border-l border-gray-100 pt-4 lg:pt-0 lg:pl-6 flex flex-col justify-between h-full">
                        <div className="space-y-2.5 text-xs">
                          <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-500 font-medium">
                              Position
                            </span>
                            <span className="font-semibold text-gray-800">
                              {result.position || index + 1}
                            </span>
                          </div>

                          <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-500 font-medium">
                              URL
                            </span>
                            <a
                              href={result.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </a>
                          </div>

                          <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-500 font-medium">
                              Source Domain
                            </span>
                            <span className="font-semibold text-gray-800">
                              {sourceName}
                            </span>
                          </div>

                          <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-500 font-medium">
                              Region
                            </span>
                            <span className="font-semibold text-gray-800">
                              {regions}
                            </span>
                          </div>

                          <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-500 font-medium">
                              Language
                            </span>
                            <span className="font-semibold text-gray-800">
                              {languages}
                            </span>
                          </div>

                          <div className="flex justify-between items-center py-1 border-b border-gray-50">
                            <span className="text-gray-500 font-medium">
                              Protocol
                            </span>
                            <span className="font-semibold text-emerald-600">
                              {isSecure}
                            </span>
                          </div>

                          <div className="flex justify-between items-center py-1">
                            <span className="text-gray-500 font-medium">
                              Matched Keywords
                            </span>
                            <span
                              className="font-semibold text-gray-800 max-w-[150px] truncate"
                              title={highlights}
                            >
                              {highlights}
                            </span>
                          </div>
                        </div>

                        {/* View Details Action */}
                        <div className="mt-6 flex justify-end">
                          <a
                            href={result.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 border border-blue-100 hover:border-blue-200 bg-blue-50/50 hover:bg-blue-50 px-3.5 py-1.5 rounded-lg transition"
                          >
                            View Details
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdsPositionTool;
